import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, QrCode, Save, Archive, ArchiveRestore, Upload } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import SidePanel from "@/components/calendar/SidePanel";
import QRCropper from "@/components/settings/QRCropper";
import { canAccessAdminSettings } from "@/lib/settingsAccess";

// =====================================================================
// Settings → QR Codes
//
// Manages the clinic's payment-method QR images (GCash, Maya, etc.).
// QR data lives on clinic_settings.payment_qr_codes (jsonb array);
// the actual image files live in Supabase storage bucket "payment-qrs".
//
// Calls:
//   list_payment_qrs(p_include_archived)
//   add_payment_qr / update_payment_qr / set_payment_qr_archived
// =====================================================================

const QR_BUCKET = "payment-qrs";

const METHOD_OPTIONS = [
  { key: "",             label: "—" },
  { key: "gcash",        label: "GCash" },
  { key: "maya",         label: "Maya" },
  { key: "bank_transfer", label: "Bank transfer" },
  { key: "other",        label: "Other" },
];

export default function SettingsQRCodes() {
  const { activeClinic, clinicRole, isPlatformAdmin } = useAuth();
  const canManageQrs = canAccessAdminSettings(clinicRole, isPlatformAdmin);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [rows,  setRows]      = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [panel, setPanel]     = useState(null);

  const load = useCallback(async () => {
    if (!canManageQrs) {
      setLoading(false);
      setError(null);
      setRows([]);
      return;
    }
    setLoading(true); setError(null);
    const { data, error: e } = await supabase.rpc("list_payment_qrs", { p_include_archived: showArchived });
    setLoading(false);
    if (e) { setError(e.message ?? "Could not load."); return; }
    setRows(Array.isArray(data) ? data : []);
  }, [canManageQrs, showArchived]);

  useEffect(() => { load(); }, [load]);

  if (!canManageQrs || (error && /permission denied/i.test(error))) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center" data-testid="qr-denied">
        <QrCode className="w-8 h-8 mx-auto text-slate-300" />
        <p className="text-sm text-slate-600 mt-3">Only a Clinic Admin can manage payment QR codes.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="settings-qrs">
      <div className="bg-white rounded-2xl shadow-sm px-5 py-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Payment QR codes</h1>
            <p className="text-xs text-slate-500">Upload GCash / Maya / bank-transfer QR images so the front desk can show them at checkout.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}
                data-testid="qr-show-archived" className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600" />
              Show archived
            </label>
            <button type="button" onClick={() => setPanel({ mode: "new" })}
              data-testid="qr-add"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded">
              <Plus className="w-3.5 h-3.5" />Add QR
            </button>
          </div>
        </div>
      </div>

      {error && !/permission denied/i.test(error) ? (
        <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
      ) : null}

      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <QrCode className="w-8 h-8 mx-auto text-slate-300" />
          <p className="text-sm text-slate-600 mt-3">No QR codes yet.</p>
          <button type="button" onClick={() => setPanel({ mode: "new" })}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded">
            <Plus className="w-3.5 h-3.5" />Add your first QR
          </button>
        </div>
      ) : (
        <ul className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100" data-testid="qr-list">
          {rows.map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => setPanel({ mode: "edit", row: r })}
                data-testid={`qr-row-${r.id}`}
                className={`w-full text-left px-5 py-3 flex items-center gap-4 hover:bg-slate-50 ${r.archived ? "opacity-60" : ""}`}>
                {r.image_url ? (
                  <img src={r.image_url} alt="QR" className="w-12 h-12 rounded border border-slate-200 object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded border border-slate-200 bg-slate-50 flex items-center justify-center"><QrCode className="w-5 h-5 text-slate-300" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-800 truncate">{r.label}</p>
                    {r.archived ? <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">Archived</span> : null}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{methodLabel(r.method)}</p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <SidePanel
        open={!!panel}
        onClose={() => setPanel(null)}
        title={panel?.mode === "new" ? "Add QR code" : "Edit QR code"}
      >
        {panel ? (
          <EditQR
            mode={panel.mode}
            row={panel.row}
            clinicId={activeClinic?.clinic_id || activeClinic?.id}
            onDone={() => { setPanel(null); load(); }}
            onCancel={() => setPanel(null)}
          />
        ) : null}
      </SidePanel>
    </div>
  );
}

function methodLabel(key) { return METHOD_OPTIONS.find((o) => o.key === key)?.label ?? key ?? "—"; }

function EditQR({ mode, row, clinicId, onDone, onCancel }) {
  const [label, setLabel]     = useState(row?.label ?? "");
  const [method, setMethod]   = useState(row?.method ?? "");
  const [imageUrl, setImageUrl] = useState(row?.image_url ?? "");
  const [busy, setBusy]       = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError]     = useState(null);
  const [pendingFile, setPendingFile] = useState(null); // file picked, awaiting crop
  const fileRef = useRef(null);

  function handleFilePicked(e) {
    const file = e.target.files?.[0];
    // reset the input so picking the same file twice still re-fires onChange
    if (e.target) e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Please choose an image file."); return; }
    setError(null);
    setPendingFile(file);
  }

  async function handleCropped(blob) {
    if (!clinicId) {
      setPendingFile(null);
      setError("Clinic context is missing. Refresh and try again.");
      return;
    }
    setPendingFile(null);
    setUploading(true); setError(null);
    const path = `${clinicId}/${crypto.randomUUID()}.png`;
    const { error: e1 } = await supabase.storage
      .from(QR_BUCKET)
      .upload(path, blob, { cacheControl: "3600", upsert: false, contentType: "image/png" });
    if (e1) { setUploading(false); setError(e1.message ?? "Upload failed."); return; }
    const { data: urlData } = supabase.storage.from(QR_BUCKET).getPublicUrl(path);
    setImageUrl(urlData?.publicUrl ?? "");
    setUploading(false);
  }

  async function handleSave() {
    if (!label.trim()) { setError("Label is required."); return; }
    if (!imageUrl)     { setError("Please upload a QR image."); return; }
    setBusy(true); setError(null);
    if (mode === "new") {
      const { error: e } = await supabase.rpc("add_payment_qr", {
        p_input: { label: label.trim(), method, image_url: imageUrl },
      });
      setBusy(false);
      if (e) { setError(e.message ?? "Save failed."); return; }
      toast.success("QR added.");
    } else {
      const { error: e } = await supabase.rpc("update_payment_qr", {
        p_id: row.id,
        p_input: { label: label.trim(), method, image_url: imageUrl },
      });
      setBusy(false);
      if (e) { setError(e.message ?? "Save failed."); return; }
      toast.success("QR updated.");
    }
    onDone?.();
  }

  async function handleArchiveToggle() {
    setBusy(true); setError(null);
    const { error: e } = await supabase.rpc("set_payment_qr_archived", {
      p_id: row.id, p_archived: !row.archived,
    });
    setBusy(false);
    if (e) { setError(e.message ?? "Failed."); return; }
    toast.success(row.archived ? "QR restored." : "QR archived.");
    onDone?.();
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="qr-label">Label</label>
          <input id="qr-label" value={label} onChange={(e) => setLabel(e.target.value)}
            data-testid="qr-label" placeholder="e.g. GCash – Main"
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="qr-method">Payment method</label>
          <select id="qr-method" value={method} onChange={(e) => setMethod(e.target.value)}
            data-testid="qr-method"
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none">
            {METHOD_OPTIONS.map((o) => <option key={o.key || "none"} value={o.key}>{o.label}</option>)}
          </select>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">QR image</label>
          {imageUrl ? (
            <div className="mt-1">
              <img src={imageUrl} alt="QR preview" className="w-40 h-40 rounded-lg border border-slate-200 object-contain bg-white" />
            </div>
          ) : null}
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFilePicked} className="hidden" data-testid="qr-image-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            data-testid="qr-upload"
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded disabled:opacity-50">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {imageUrl ? "Replace image" : "Upload image"}
          </button>
          <p className="mt-1.5 text-[11px] text-slate-400">You'll crop it down to just the QR square in the next step.</p>
        </div>

        {error ? (
          <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
        ) : null}
      </div>

      <div className="border-t border-slate-200 px-5 py-3 flex items-center gap-2 bg-white">
        {mode === "edit" ? (
          <button type="button" onClick={handleArchiveToggle} disabled={busy}
            data-testid="qr-archive"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded disabled:opacity-50">
            {row.archived ? <><ArchiveRestore className="w-3.5 h-3.5" />Restore</> : <><Archive className="w-3.5 h-3.5" />Archive</>}
          </button>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={onCancel} disabled={busy}
            className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 rounded">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={busy || uploading}
            data-testid="qr-save"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>
      </div>

      {pendingFile ? (
        <QRCropper
          file={pendingFile}
          onCancel={() => setPendingFile(null)}
          onCropped={handleCropped}
        />
      ) : null}
    </div>
  );
}
